import { getPageUrlDic, Page } from '../routes';
import { WhitelabelConfig } from '@xarvio/frontend-core-state/lib/auth/hooks/useWhitelabel/useWhitelabel';

export interface GetPagesUrlProps<
  TQueryParameters extends Record<string, string>,
> {
  farmUuid?: string;
  fieldUuid?: string;
  cropSeasonUuid?: string;
  country?: string;
  locale?: string;
  queryParameters?: TQueryParameters;
  forRouting?: boolean;
}

export const getPagesUrl = <TQueryParameters extends Record<string, string>>(
  {
    country,
    cropSeasonUuid,
    farmUuid,
    fieldUuid,
    forRouting,
    locale,
    queryParameters,
  }: GetPagesUrlProps<TQueryParameters>,
  whitelabelConfig?: WhitelabelConfig,
) => {
  let prefix = '';

  if (country && locale) {
    prefix = `/${country}/${locale}`;
  } else if (country) {
    prefix = `/${country}`;
  } else if (locale) {
    prefix = `/${locale}`;
  }

  let urlQueryParams = '';

  if (queryParameters) {
    urlQueryParams = `?${new URLSearchParams(queryParameters).toString()}`;
  }

  const pageUrlDic = getPageUrlDic({
    prefix,
    farmUuid,
    cropSeasonUuid,
    fieldUuid,
    urlQueryParams,
    forRouting,
  });

  const {
    [Page.LANDING]: landingPageUrl,
    [Page.PAYMENT_SUCCESS]: paymentSuccessUrl,
    [Page.PAYMENT_FAILED]: paymentFailedUrl,
    [Page.SMART_SPRAYER]: smartSprayerPageUrl,
    [Page.SMART_SPRAYER_NEW]: smartSprayerPageNewUrl,
    [Page.SMART_SPRAYER_LANDING]: smartSprayerLandingPageUrl,
    [Page.SMART_SPRAYER_REGISTRATION]: smartSprayerRegistrationPageUrl,
    [Page.SMART_SPRAYER_USER_SETTINGS]: smartSprayerUserSettingsPageUrl,
    [Page.LOGIN]: loginPageUrl,
    [Page.REGISTRATION]: registrationPageUrl,
    [Page.FORGOT_PASSWORD]: forgotPasswordPageUrl,
    [Page.RESET_PASSWORD]: resetPasswordPageUrl,
    [Page.EMAIL_VERIFIED]: emailVerifiedPageUrl,
    [Page.ACCEPT_TC]: acceptTcPageUrl,
    [Page.REDIRECT_XARVIO_CONNECT_ONBOARDING]: redirectXarvioConnectOnboarding,
    [Page.ORGANISATION_INVITATION]: organisationInvitationUrl,
    [Page.STATUS]: statusPageUrl,
    [Page.STATUS_NEW_ALL_FIELDS]: newStatusPageAllFieldsUrl,
    [Page.STATUS_NEW_RECOMMENDATIONS]: newStatusPageRecommendationsUrl,
    [Page.STATUS_NEW_CROP_OVERVIEW]: newStatusPageCropOverviewUrl,
    [Page.STATUS_NEW_CROP_FIELDS]: newStatusPageCropFieldsUrl,
    [Page.STATUS_NEW_CROP_NUTRITION]: newStatusPageCropNutritionUrl,
    [Page.STATUS_NEW_CROP_WEED_MANAGEMENT]: newStatusPageCropWeedManagementUrl,
    [Page.STATUS_NEW_FIELD_DETAILS]: newStatusPageFieldDetailsUrl,
    [Page.STATUS_NEW_UNASSIGNED_FIELDS]: newStatusPageUnassignedFieldsUrl,
    [Page.STATUS_TABLE_ALL_FIELDS_GENERAL]: tableStatusPageAllFieldsGeneralUrl,
    [Page.TASK]: taskPageUrl,
    [Page.WEATHER]: weatherPageUrl,
    [Page.HISTORY]: historyPageUrl,
    [Page.HISTORY_CS]: historyCsPageUrl,
    [Page.NEW_HISTORY_CROP_DETAILS]: newHistoryCropDetailsPageUrl,
    [Page.ANALYTICS]: analyticsPageUrl,
    [Page.NEW_ANALYTICS]: newAnalyticsPageUrl,
    [Page.SHOP_PRODUCTS]: shopPageUrl,
    [Page.SHOP_PRODUCTS_PURCHASE]: shopProductsPageNewPurchaseUrl,
    [Page.SHOP_PRODUCTS_ACTIVE_LICENSES]: shopProductsPageNewActiveLicensesUrl,
    [Page.SHOP_PRODUCTS_ORDER_HISTORY]: shopProductsPageNewOrderHistoryUrl,
    [Page.SHOP_PRODUCTS_SUBSCRIPTIONS]: shopProductsPageNewSubscriptionsUrl,
    [Page.SHOP_ORDER]: shopOrderPageNewUrl,
    [Page.HEALTHY_FIELDS]: healthyFieldsPageUrl,
    [Page.HEALTHY_FIELDS_CLAIMS]: healthyFieldsClaimsPageUrl,
    [Page.USER_PROFILE]: userProfileSettingsPageUrl,
    [Page.FARM_SETTINGS]: farmSettingsPageUrl,
    [Page.COLLABORATION_SETTINGS]: collaborationSettingsPageUrl,
    [Page.CONNECTED_APP_SETTINGS]: connectedAppSettingsPageUrl,
    [Page.WEATHER_STATION_SETTINGS]: weatherStationSettingsPageUrl,
    [Page.DATA_ATTRIBUTION_SETTINGS]: dataAttributionSettingsPageUrl,
    [Page.EXPERT_VIEW]: expertViewPageUrl,
    [Page.SEEDING_TASK_EDIT]: seedingTaskEditUrl,
    [Page.SEEDING_TASK_CREATION]: seedingTaskCreationUrl,
    [Page.SCOUTING_TASK_EDIT]: scoutingTaskEditUrl,
    [Page.SCOUTING_TASK_CREATION]: scoutingTaskCreationUrl,
    [Page.SEED_TREATMENT_TASK_CREATION]: seedTreatmentTaskCreationUrl,
    [Page.SEED_TREATMENT_TASK_EDIT]: seedTreatmentTaskEditUrl,
    [Page.SEEDBOX_TREATMENT_TASK_CREATION]: seedboxTreatmentTaskCreationUrl,
    [Page.SEEDBOX_TREATMENT_TASK_EDIT]: seedboxTreatmentTaskEditUrl,
    [Page.CROSS_FARM_DASHBOARD]: crossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_GENERAL]: generalCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_RISKS]: risksCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_SCOUTING]: scoutingUrl,
    [Page.CROSS_FARM_DASHBOARD_PROTOCOLS]: scoutingProtocolsUrl,
    [Page.CROSS_FARM_DASHBOARD_TRIPS]: scoutingTripsUrl,
    [Page.CROP_SEASON_EDIT]: cropSeasonEditUrl,
    [Page.CROP_SEASON_ASSIGN]: cropSeasonAssignUrl,
    [Page.CROSS_FARM_DASHBOARD_TASKS]: tasksCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_SOIL]: soilCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_CROP_DETAILS]: cropDetailsCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_FIELD_TRIALS]: fieldTrialsCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_HEALTHY_FIELDS_OVERVIEW]:
      healthyFieldsRecommendationsCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_HEALTHY_FIELDS__COMMERCIAL_PARTNER]:
      cfdHfCommercialPartnerUrl,
    [Page.CROSS_FARM_DASHBOARD_HEALTHY_FIELDS__SEASON_COORDINATOR]:
      cfdHfSeasonCoordinatorUrl,
    [Page.CROSS_FARM_DASHBOARD_HEALTHY_FIELDS__AGRONOMIST]: cfdHfAgronomistUrl,
    [Page.CROSS_FARM_DASHBOARD_HEALTHY_FIELDS__CONTRACTOR]: cfdHfContractorUrl,
    [Page.CROSS_FARM_DASHBOARD_FIELD_NOTES]: fieldNotesCrossFarmUrl,
    [Page.CROSS_FARM_DASHBOARD_SMART_SPRAYER]: smartSprayerCrossFarmUrl,
    [Page.FDL_UPLOAD]: fdlUploadUrl,
    [Page.NOTES]: notesPageUrl,
    [Page.FIELD_DETECT]: detectFieldPageUrl,
    [Page.FIELD_UPLOAD]: uploadFieldPageUrl,
    [Page.EDIT_FIELD]: editFieldPageUrl,
    [Page.ONBOARDING_ADD_FIELD]: onboardingAddFieldPageUrl,
    [Page.ONBOARDING_ADD_FIELD_HF]: onboardingAddFieldHfPageUrl,
    [Page.ONBOARDING_ASSIGN_CROPSEASON]: onboardingAssignCropseasonPageUrl,
    [Page.EXATREK_SETTINGS]: exatrekSettingsPageUrl,
    [Page.WATER_MANAGEMENT_TASK_EDIT]: waterManagementTaskEditUrl,
    [Page.WATER_MANAGEMENT_TASK_CREATION]: waterManagementTaskCreationUrl,
    [Page.CROP_SEASON_TREATMENT_PROGRAMS]: cropSeasonTreatmentProgramsUrl,
    [Page.CROP_SEASON_TREATMENT_PROGRAM_CREATION]:
      cropSeasonTreatmentProgramCreationUrl,
    [Page.CROP_SEASON_TREATMENT_PROGRAM_RECOMMENDATIONS]:
      cropSeasonTreatmentProgramRecommendationsUrl,
    [Page.LAND_MANAGEMENT_TASK_EDIT]: landPreparationTaskEditUrl,
    [Page.LAND_PREPARATION_CREATION]: landPreparationTaskCreationUrl,
    [Page.HARVEST_TASK_EDIT]: harvestTaskEditUrl,
    [Page.HARVEST_TASK_CREATION]: harvestTaskCreationUrl,
    [Page.HERBICIDE_TASK_CREATION]: herbicideTaskCreationNewUrl,
    [Page.HERBICIDE_TASK_EDIT]: herbicideTaskEditNewUrl,
    [Page.SUSTAINABILITY]: sustainabilityPageUrl,
    [Page.BIOENERGY]: bioenergyPageUrl,
    [Page.BIOENERGY_COMPANY]: bioenergyCompanyPageUrl,
    [Page.BIOENERGY_JOIN]: bioenergyJoinPageUrl,
    [Page.BIOENERGY_ENROLLMENT]: bioenergyEnrollmentPageUrl,
    [Page.BIOENERGY_ENROLLMENT_DETAILS]: bioenergyEnrollmentDetailsPageUrl,
    [Page.BIOENERGY_NEW_PROGRAM]: bioenergyNewProgramPageUrl,
    [Page.BIOENERGY_ENROLLMENT_SELECT_FIELDS]:
      bioenergyEnrollmentSelectFieldsPageUrl,
    [Page.BIOENERGY_ENROLLMENT_CONFIRM]: bioenergyEnrollmentConfirmPageUrl,
    [Page.BIOENERGY_CFD]: bioenergyCfdPageUrl,
    [Page.BIOENERGY_CFD_COMPANY]: bioenergyCfdCompanyPageUrl,
    [Page.BIOENERGY_CFD_NEW_PROGRAM]: bioenergyCfdNewProgramPageUrl,
    [Page.MANAGEMENT_ZONES]: managementZonesUrl,
    [Page.MANAGEMENT_ZONES_DRAW]: managementZonesDrawUrl,
    [Page.MANAGEMENT_AB_LINES]: managementABLinesUrl,
    [Page.MANAGEMENT_AB_LINES_DRAW]: managementABLinesDrawUrl,
    [Page.MANAGEMENT_FIELD_ENTRIES]: managementFieldEntriesUrl,
    [Page.MANAGEMENT_FIELD_ENTRIES_DRAW]: managementFieldEntriesDrawUrl,
    [Page.MANAGEMENT_GUIDANCE_PATTERN]: managementGuidancePatternUrl,
    [Page.MANAGEMENT_GUIDANCE_PATTERN_DRAW]: managementGuidancePatternDrawUrl,
    [Page.MANAGEMENT_ZONES_NEW]: managementZonesPageUrl,
    [Page.DRONE_FLIGHT_TASK_EDIT]: droneFlightTaskEditUrl,
    [Page.DRONE_FLIGHT_TASK_CREATION]: droneFlightTaskCreateUrl,
    [Page.NUTRITION_TASK_EDIT]: nutritionTaskEditUrl,
    [Page.NUTRITION_TASK_CREATION]: nutritionTaskCreateUrl,
    [Page.CROP_PROTECTION_TASK_EDIT]: cropProtectionTaskEditUrl,
    [Page.CROP_PROTECTION_TASK_CREATION]: cropProtectionTaskCreateUrl,
    [Page.NUTRITION_TASK_EDIT_NEW]: nutritionTaskEditNewUrl,
    [Page.NUTRITION_TASK_CREATION_NEW]: nutritionTaskCreateNewUrl,
    [Page.SCOUTING_TASK_EDIT_NEW]: scoutingTaskEditNewUrl,
    [Page.SCOUTING_TASK_CREATION_NEW]: scoutingTaskCreationNewUrl,
    [Page.HEALTHY_FIELDS_OVERVIEW_NEW]: healthyFieldsOverviewPageNewUrl,
    [Page.HEALTHY_FIELDS_SERVICES_NEW]: healthyFieldsServicesPageNewUrl,
    [Page.HEALTHY_FIELDS_REQUEST_NEW]: healthyFieldsRequestsPageNewUrl,
    [Page.HEALTHY_FIELDS_CONTRACTOR_REQUEST_NEW]:
      healthyFieldsContractorRequestsPageNewUrl,
    [Page.HEALTHY_FIELDS_OFFER_ACCEPT_OR_REJECT]:
      healthyFieldsOfferAcceptOrRejectPageUrl,
    [Page.HEALTHY_FIELDS_FIELD_REGISTRATION]:
      healthyFieldsFieldRegistrationPageUrl,
    [Page.HEALTHY_FIELDS_FIELD_REGISTRATION_GREENLEAF_CZ]:
      healthyFieldsFieldRegistrationGreenleafCzPageUrl,
    [Page.HEALTHY_FIELDS_FIELD_REGISTRATION_COTEGRA_CA]:
      healthyFieldsFieldRegistrationCotegraCAPageUrl,
    [Page.HEALTHY_FIELDS_FIELD_REGISTRATION_BRAZIL]:
      healthyFieldsFieldRegistrationBrazilPageUrl,
    [Page.CONNECTED_OFFERS]: connectedOffersPageUrl,
    [Page.PLANTED_HECTARES_FIELD_REGISTRATION]:
      plantedHectaresFieldRegistrationPageUrl,
    [Page.MAP_IMPORT]: mapImportUrl,
    [Page.WHATS_NEW]: whatsNewPageUrl,
    [Page.NUTRITION_PLANNING]: nutritionPlanningPageUrl,
    [Page.HEALTHY_FIELDS_COMMERCIAL_PARTNER_NEW]:
      healthyFieldsCommercialPartnerPageNewUrl,
    [Page.HEALTHY_FIELDS_SEASON_COORDINATOR_NEW]:
      healthyFieldsSeasonCoordinatorPageNewUrl,
    [Page.HEALTHY_FIELDS_AGRONOMIST_NEW]: healthyFieldsAgronomistPageNewUrl,
    [Page.HEALTHY_FIELDS_CONTRACTOR_NEW]: healthyFieldsContractorPageNewUrl,
    [Page.NUTRITION_PLANNING_PROGRAM_CREATION]: nutritionPageProgramCreationUrl,
    [Page.NUTRITION_PLANNING_N_PILOTING_CREATION]: nutritionPilotingCreationUrl,
    [Page.NUTRITION_PLANNING_MAP_CREATION]: nutritionPlanningMapCreationUrl,
    [Page.SEED_VARIETY_RECOMMENDATION]: seedVarietyRecommendationsUrl,
    [Page.CARBON_FARMING]: carbonFarmingPageUrl,
    [Page.CARBON_FARMING_ENROLLMENT]: carbonFarmingEnrollmentPageUrl,
    [Page.ADD_TEST_RESULT]: addTestResultUrl,
    [Page.CARBON_FARMING_OVERVIEW]: carbonFarmingOverviewUrl,
    [Page.CARBON_FARMING_TIMELINE]: carbonFarmingTimelineUrl,
    [Page.CARBON_FARMING_REPORTS]: carbonFarmingReportsUrl,
    [Page.CARBON_FARMING_STATISTICS]: carbonFarmingStatisticsUrl,
    [Page.CARBON_FARMING_CFD]: carbonFarmingCfdUrl,
    [Page.MULTI_SEASON_NUTRITION_PLANNING_PROGRAM_CREATION]:
      multiSeasonNutritionPageProgramCreationUrl,
    [Page.ADD_PROTOCOL]: addProtocolPageUrl,
    [Page.ADD_TRIAL]: addTrialPageUrl,
    [Page.KUBOTA_FIELD_IMPORT]: kubotaFieldImportPageUrl,

    [Page.BUSINESS_LINES]: businessLinesUrl,
    [Page.BUSINESS_LINES_OVERVIEW]: businessLinesOverviewUrl,
    [Page.BUSINESS_LINES_LICENSES_OVERVIEW]: businessLinesLicensesOverviewUrl,
    [Page.BUSINESS_LINES_LICENSES_REQUESTS]: businessLinesLicensesRequestsUrl,
    [Page.QUOTA_TRANSFER]: quotaTransferUrl,
    [Page.ORGANISATION_MEMBERS]: organisationMembersUrl,
    [Page.ORGANISATION_INVITATIONS]: organisationInvitationsUrl,
    [Page.ORGANISATION_SETTINGS]: organisationSettingsUrl,
    [Page.ORGANISATION_SETTINGS_VIEW]: organisationSettingsViewUrl,
    [Page.ORGANISATION_LOCATIONS]: organisationLocationsUrl,
    [Page.BUSINESS_LINES_FARMS]: businessLinesFarmsUrl,
    [Page.AGRONOMY_DASHBOARD]: agronomyDashboardPageUrl,
    [Page.AGRONOMY_CARBON_FARMING]: agronomyCarbonFarmingUrl,
    [Page.AGRONOMY_BARILLA]: agronomyBarillaUrl,
    [Page.AGRONOMY_PERONI]: agronomyPeroniUrl,
    [Page.AGRONOMY_COTEGRA]: agronomyCotegraUrl,
    [Page.AGRONOMY_NEMAFREE]: agronomyNemaFreeUrl,
    [Page.AGRONOMY_WEED_ZONE_SPRAY]: agronomyWeedZoneSprayUrl,
    [Page.CALCULATE_NUTRITION_DEMAND]: calculateNutritionDemandPageUrl,
    [Page.AUTO_LOGIN]: autoLoginPageUrl,
    [Page.BARILLA_ONBOARDING]: barillaOnboardingPageUrl,
    [Page.PERONI_ONBOARDING]: peroniOnboardingPageUrl,
    [Page.HORTA_GRAPE_ONBOARDING]: hortaGrapeOnboardingPageUrl,
    [Page.GRAPES_ONBOARDING_ADD_FIELDS]: grapesOnboardingAddFieldsPageUrl,
    [Page.GRAPES_ONBOARDING_ASSIGN_CROP]: grapesOnboardingAssignCropPageUrl,
    [Page.HORTA_NUTRITION_DEMAND]: hortaNutritionDemandUrl,
    [Page.STATUS_NEW_ALL_FIELDS_NUTRITION]: newStatusPageAllFieldsNutritionUrl,
    [Page.HORTA_NUTRITION_PLANNING_PROGRAM_CREATION]:
      hortaNutritionPlanningProgramCreationUrl,
  } = pageUrlDic;

  const retObj = {
    landingPageUrl,
    paymentSuccessUrl,
    paymentFailedUrl,
    smartSprayerPageUrl,
    smartSprayerPageNewUrl,
    smartSprayerLandingPageUrl,
    smartSprayerRegistrationPageUrl,
    smartSprayerUserSettingsPageUrl,
    loginPageUrl,
    autoLoginPageUrl,
    registrationPageUrl,
    forgotPasswordPageUrl,
    resetPasswordPageUrl,
    emailVerifiedPageUrl,
    acceptTcPageUrl,
    redirectXarvioConnectOnboarding,
    organisationInvitationUrl,
    statusPageUrl,
    taskPageUrl,
    weatherPageUrl,
    historyPageUrl,
    historyCsPageUrl,
    newHistoryCropDetailsPageUrl,
    analyticsPageUrl,
    newAnalyticsPageUrl,
    shopPageUrl,
    shopProductsPageNewPurchaseUrl,
    shopProductsPageNewActiveLicensesUrl,
    shopProductsPageNewOrderHistoryUrl,
    shopProductsPageNewSubscriptionsUrl,
    shopOrderPageNewUrl,
    healthyFieldsPageUrl,
    healthyFieldsClaimsPageUrl,
    userProfileSettingsPageUrl,
    farmSettingsPageUrl,
    collaborationSettingsPageUrl,
    connectedAppSettingsPageUrl,
    weatherStationSettingsPageUrl,
    dataAttributionSettingsPageUrl,
    expertViewPageUrl,
    cropSeasonEditUrl,
    cropSeasonAssignUrl,
    seedingTaskEditUrl,
    seedingTaskCreationUrl,
    scoutingTaskEditUrl,
    scoutingTaskCreationUrl,
    seedTreatmentTaskCreationUrl,
    seedTreatmentTaskEditUrl,
    seedboxTreatmentTaskCreationUrl,
    seedboxTreatmentTaskEditUrl,
    crossFarmUrl,
    generalCrossFarmUrl,
    risksCrossFarmUrl,
    tasksCrossFarmUrl,
    soilCrossFarmUrl,
    cropDetailsCrossFarmUrl,
    healthyFieldsRecommendationsCrossFarmUrl,
    cfdHfCommercialPartnerUrl,
    cfdHfSeasonCoordinatorUrl,
    cfdHfAgronomistUrl,
    cfdHfContractorUrl,
    fieldNotesCrossFarmUrl,
    smartSprayerCrossFarmUrl,
    scoutingUrl,
    scoutingProtocolsUrl,
    scoutingTripsUrl,
    fdlUploadUrl,
    notesPageUrl,
    detectFieldPageUrl,
    uploadFieldPageUrl,
    editFieldPageUrl,
    onboardingAddFieldPageUrl,
    onboardingAddFieldHfPageUrl,
    onboardingAssignCropseasonPageUrl,
    exatrekSettingsPageUrl,
    waterManagementTaskEditUrl,
    waterManagementTaskCreationUrl,
    cropSeasonTreatmentProgramsUrl,
    cropSeasonTreatmentProgramCreationUrl,
    cropSeasonTreatmentProgramRecommendationsUrl,
    landPreparationTaskCreationUrl,
    landPreparationTaskEditUrl,
    harvestTaskEditUrl,
    harvestTaskCreationUrl,
    herbicideTaskCreationNewUrl,
    herbicideTaskEditNewUrl,
    sustainabilityPageUrl,
    bioenergyPageUrl,
    bioenergyCompanyPageUrl,
    bioenergyJoinPageUrl,
    bioenergyEnrollmentPageUrl,
    bioenergyEnrollmentDetailsPageUrl,
    bioenergyNewProgramPageUrl,
    bioenergyEnrollmentSelectFieldsPageUrl,
    bioenergyEnrollmentConfirmPageUrl,
    bioenergyCfdPageUrl,
    bioenergyCfdCompanyPageUrl,
    bioenergyCfdNewProgramPageUrl,
    managementZonesUrl,
    managementZonesDrawUrl,
    managementABLinesUrl,
    managementABLinesDrawUrl,
    managementFieldEntriesUrl,
    managementFieldEntriesDrawUrl,
    managementGuidancePatternUrl,
    managementGuidancePatternDrawUrl,
    managementZonesPageUrl,
    droneFlightTaskEditUrl,
    droneFlightTaskCreateUrl,
    nutritionTaskEditUrl,
    nutritionTaskCreateUrl,
    cropProtectionTaskEditUrl,
    cropProtectionTaskCreateUrl,
    nutritionTaskEditNewUrl,
    nutritionTaskCreateNewUrl,
    scoutingTaskEditNewUrl,
    scoutingTaskCreationNewUrl,
    healthyFieldsOverviewPageNewUrl,
    healthyFieldsServicesPageNewUrl,
    healthyFieldsRequestsPageNewUrl,
    healthyFieldsContractorRequestsPageNewUrl,
    healthyFieldsOfferAcceptOrRejectPageUrl,
    mapImportUrl,
    nutritionPlanningPageUrl,
    whatsNewPageUrl,
    healthyFieldsCommercialPartnerPageNewUrl,
    healthyFieldsSeasonCoordinatorPageNewUrl,
    healthyFieldsAgronomistPageNewUrl,
    healthyFieldsContractorPageNewUrl,
    healthyFieldsFieldRegistrationPageUrl,
    healthyFieldsFieldRegistrationGreenleafCzPageUrl,
    healthyFieldsFieldRegistrationCotegraCAPageUrl,
    healthyFieldsFieldRegistrationBrazilPageUrl,
    connectedOffersPageUrl,
    plantedHectaresFieldRegistrationPageUrl,
    newStatusPageAllFieldsUrl,
    newStatusPageRecommendationsUrl,
    newStatusPageCropOverviewUrl,
    newStatusPageCropFieldsUrl,
    newStatusPageCropNutritionUrl,
    newStatusPageCropWeedManagementUrl,
    newStatusPageFieldDetailsUrl,
    newStatusPageUnassignedFieldsUrl,
    tableStatusPageAllFieldsGeneralUrl,
    nutritionPageProgramCreationUrl,
    nutritionPilotingCreationUrl,
    nutritionPlanningMapCreationUrl,
    multiSeasonNutritionPageProgramCreationUrl,
    seedVarietyRecommendationsUrl,
    carbonFarmingPageUrl,
    carbonFarmingEnrollmentPageUrl,
    addTestResultUrl,
    carbonFarmingOverviewUrl,
    carbonFarmingTimelineUrl,
    carbonFarmingReportsUrl,
    carbonFarmingStatisticsUrl,
    carbonFarmingCfdUrl,
    fieldTrialsCrossFarmUrl,
    addProtocolPageUrl,
    addTrialPageUrl,
    kubotaFieldImportPageUrl,
    businessLinesUrl,
    businessLinesOverviewUrl,
    businessLinesLicensesOverviewUrl,
    businessLinesLicensesRequestsUrl,
    quotaTransferUrl,
    organisationMembersUrl,
    organisationInvitationsUrl,
    organisationSettingsUrl,
    organisationSettingsViewUrl,
    organisationLocationsUrl,
    businessLinesFarmsUrl,
    agronomyDashboardPageUrl,
    agronomyCarbonFarmingUrl,
    agronomyBarillaUrl,
    agronomyPeroniUrl,
    agronomyCotegraUrl,
    agronomyNemaFreeUrl,
    agronomyWeedZoneSprayUrl,
    calculateNutritionDemandPageUrl,
    barillaOnboardingPageUrl,
    peroniOnboardingPageUrl,
    hortaGrapeOnboardingPageUrl,
    grapesOnboardingAddFieldsPageUrl,
    grapesOnboardingAssignCropPageUrl,
    hortaNutritionDemandUrl,
    newStatusPageAllFieldsNutritionUrl,
    hortaNutritionPlanningProgramCreationUrl,
  };

  if (whitelabelConfig) {
    if (whitelabelConfig.blacklistedRoutes) {
      for (const route of whitelabelConfig.blacklistedRoutes) {
        delete retObj[route];
      }
    }

    if (whitelabelConfig.whitelistedRoutes) {
      for (const route of Object.keys(retObj)) {
        if (!whitelabelConfig.whitelistedRoutes.includes(route as any)) {
          delete retObj[route];
        }
      }
    }
  }
  return retObj;
};
